import React, { useEffect, useState } from "react";
import { Mail, Search, Trash2, Check, X, RefreshCw } from "lucide-react";
import { adminContacts, adminPatchContact, adminDeleteContact } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { useToast } from "../../hooks/use-toast";

export default function DashboardContacts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminContacts();
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRead = async (c) => {
    try {
      const { data } = await adminPatchContact(c.id, { read: !c.read });
      setRows((prev) => prev.map((r) => (r.id === c.id ? data : r)));
      if (active?.id === c.id) setActive(data);
    } catch (e) {
      toast({ title: "Failed to update contact" });
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete message from ${c.name}?`)) return;
    try {
      await adminDeleteContact(c.id);
      setRows((prev) => prev.filter((r) => r.id !== c.id));
      if (active?.id === c.id) setActive(null);
    } catch (e) {
      toast({ title: "Failed to delete" });
    }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.name, r.email, r.company, r.message].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="kicker">Inbox</div>
          <h1 className="mt-2 font-display text-3xl font-bold text-white">Contacts</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts..." className="pl-9 h-10 w-72 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-500" />
          </div>
          <button onClick={load} className="h-10 px-3 rounded-lg border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-white flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 glass rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No contact messages found.</div>
          ) : (
            <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActive(c)}
                  className={`w-full text-left px-4 py-3 transition ${active?.id === c.id ? "bg-cyan-500/10" : "hover:bg-white/5"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-white text-sm truncate">{c.name}</div>
                    {!c.read && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 uppercase tracking-widest shrink-0">New</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{c.email}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.message}</div>
                  <div className="text-[10px] text-slate-600 mt-1">{new Date(c.created_at).toLocaleString()}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3 glass rounded-2xl min-h-[70vh]">
          {!active ? (
            <div className="h-full flex items-center justify-center text-slate-500 p-10">
              <div className="text-center">
                <Mail className="w-8 h-8 mx-auto mb-3 text-cyan-400/50" />
                <div>Select a contact to view details</div>
              </div>
            </div>
          ) : (
            <div className="p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold text-white">{active.name}</h2>
                  <div className="mt-1 text-sm text-cyan-300">{active.email}</div>
                  {active.company && <div className="mt-0.5 text-xs text-slate-400">{active.company}</div>}
                  <div className="mt-1 text-[11px] text-slate-500">{new Date(active.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleRead(active)} className="h-9 px-3 rounded-lg border border-white/10 hover:border-cyan-400/40 text-slate-200 flex items-center gap-2 text-sm">
                    {active.read ? <><X className="w-4 h-4" /> Mark Unread</> : <><Check className="w-4 h-4" /> Mark Read</>}
                  </button>
                  <button onClick={() => remove(active)} className="h-9 px-3 rounded-lg border border-red-400/20 hover:border-red-400/50 text-red-300 flex items-center gap-2 text-sm">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>
              <div className="mt-6 p-5 rounded-xl bg-white/[0.02] border border-white/5 text-slate-200 whitespace-pre-wrap leading-relaxed">
                {active.message}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <a href={`mailto:${active.email}`} className="btn-primary rounded-xl h-11 px-5 font-semibold border-0 inline-flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Reply via Email
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
